import React, { useState, useEffect } from 'react';
import { profesoresAPI, asignaturasAPI, instrumentosAPI } from '../../services/api';
import { compareEs } from '../../services/utils';

const ClassForm = ({ classData, onSubmit, onCancel, currentYear }) => {
  const [formData, setFormData] = useState({
    Curso: currentYear,
    AsignaturaID: '',
    ProfesorID: '',
    InstrumentoID: null,
    DiaSemana: '1',
    H_Inicio: '09:00',
    H_Final: '10:00'
  });

  const [professors, setProfessors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
    if (classData) {
      setFormData({
        ...classData,
        InstrumentoID: classData.InstrumentoID ?? null,
        H_Inicio: classData.H_Inicio?.substring(0, 5) || '09:00',
        H_Final: classData.H_Final?.substring(0, 5) || '10:00'
      });
    }
  }, [classData, currentYear]);

  const fetchData = async () => {
    try {
      const [professorsRes, subjectsRes, instrumentsRes] = await Promise.all([
        profesoresAPI.getAll(),
        asignaturasAPI.getAll(),
        instrumentosAPI.getAll()
      ]);
      
      setProfessors(professorsRes.data);
      setSubjects(subjectsRes.data.sort((a, b) => compareEs(a.Nombre, b.Nombre)));
      setInstruments(instrumentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'InstrumentoID' && value === '' ? null : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
    
    // Validación de tiempo
    if (name === 'H_Inicio' || name === 'H_Final') {
      if (name === 'H_Inicio' && formData.H_Final && value >= formData.H_Final) {
        setErrors(prev => ({ ...prev, H_Inicio: 'La hora de inicio debe ser anterior a la hora de fin' }));
      } else if (name === 'H_Final' && formData.H_Inicio && value <= formData.H_Inicio) {
        setErrors(prev => ({ ...prev, H_Final: 'La hora de fin debe ser posterior a la hora de inicio' }));
      } else {
        setErrors(prev => ({ ...prev, H_Inicio: undefined, H_Final: undefined }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (!formData.AsignaturaID) newErrors.AsignaturaID = 'Asignatura es requerida';
    if (!formData.ProfesorID) newErrors.ProfesorID = 'Profesor es requerido';
    if (!formData.DiaSemana) newErrors.DiaSemana = 'Día de la semana es requerido';
    if (!formData.H_Inicio) newErrors.H_Inicio = 'Hora de inicio es requerida';
    if (!formData.H_Final) newErrors.H_Final = 'Hora de fin es requerida';
    
    if (formData.H_Inicio && formData.H_Final && formData.H_Inicio >= formData.H_Final) {
      newErrors.H_Inicio = 'La hora de inicio debe ser anterior a la hora de fin';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit(formData);
  };

  const daysOfWeek = [
    { value: '1', label: 'Lunes' },
    { value: '2', label: 'Martes' },
    { value: '3', label: 'Miércoles' },
    { value: '4', label: 'Jueves' },
    { value: '5', label: 'Viernes' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Asignatura */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Asignatura *
          </label>
          <select
            name="AsignaturaID"
            value={formData.AsignaturaID}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.AsignaturaID ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          >
            <option value="">Seleccionar asignatura</option>
            {subjects.map(subject => (
              <option key={subject.IDAsignatura} value={subject.IDAsignatura}>
                {subject.Nombre} ({subject.Nivel === null || subject.Nivel === undefined ? '-' : `Nivel ${subject.Nivel}`})
              </option>
            ))}
          </select>
          {errors.AsignaturaID && (
            <p className="mt-1 text-sm text-red-600">{errors.AsignaturaID}</p>
          )}
        </div>

        {/* Profesor */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Profesor *
          </label>
          <select
            name="ProfesorID"
            value={formData.ProfesorID}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.ProfesorID ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          >
            <option value="">Seleccionar profesor</option>
            {professors.map(professor => (
              <option key={professor.IDProfesor} value={professor.IDProfesor}>
                {professor.Nombre} {professor.Apellido}
              </option>
            ))}
          </select>
          {errors.ProfesorID && (
            <p className="mt-1 text-sm text-red-600">{errors.ProfesorID}</p>
          )}
        </div>

        {/* Instrumento */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Instrumento
          </label>
          <select
            name="InstrumentoID"
            value={formData.InstrumentoID}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="">Seleccionar instrumento</option>
            {instruments.map(instrument => (
              <option key={instrument.IDInstrumento} value={instrument.IDInstrumento}>
                {instrument.Nombre}
              </option>
            ))}
          </select>
        </div>

        {/* Día de la semana */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Día de la semana *
          </label>
          <select
            name="DiaSemana"
            value={formData.DiaSemana}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.DiaSemana ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          >
            {daysOfWeek.map(day => (
              <option key={day.value} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
          {errors.DiaSemana && (
            <p className="mt-1 text-sm text-red-600">{errors.DiaSemana}</p>
          )}
        </div>

        {/* Hora de inicio */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Hora de inicio *
          </label>
          <input
            type="time"
            name="H_Inicio"
            value={formData.H_Inicio}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.H_Inicio ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.H_Inicio && (
            <p className="mt-1 text-sm text-red-600">{errors.H_Inicio}</p>
          )}
        </div>

        {/* Hora de fin */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Hora de fin *
          </label>
          <input
            type="time"
            name="H_Final"
            value={formData.H_Final}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.H_Final ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.H_Final && (
            <p className="mt-1 text-sm text-red-600">{errors.H_Final}</p>
          )}
        </div>
      </div>

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          {classData ? 'Actualizar' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};

export default ClassForm;